import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router';
import { Plus, Search, AlertCircle } from 'lucide-react';
import { addTask, updateTask } from '../store';
import { Task, TaskType, Project } from '../types';
import TaskCard from '../components/TaskCard';
import CreateTaskModal from '../components/CreateTaskModal';
import TaskDetailsModal from '../components/TaskDetailsModal';
import SurfaceCard from '../components/SurfaceCard';
import { useApp } from '../providers/AppProvider';
import { useOrganizationData } from '../hooks/useOrganizationData';
import {
  canCreateProjectTasks,
  canEditTaskParameters,
  canDeleteProjectTask,
  getProjectTeamMemberIds,
  getProjectTeamUsers,
  isExternalPartnerUser,
} from '../utils/projectAccess';
import { sortByTaskOrder } from '../utils/taskFiltering';
import { APP_FILTER_LABEL_CLASS, APP_NATIVE_FIELD_CLASS } from '../constants/formControls';

export default function TaskBoard() {
  const { projectId } = useParams();
  
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<TaskType | 'All'>('All');

  const { state, loading, reload } = useApp();
  const { projects, tasks, orgId } = useOrganizationData(state) as {
    projects: Project[];
    tasks: Task[];
    orgId?: string;
  };

  const project = useMemo(() => {
    return projects.find(
      (p: Project) => String(p.id).toLowerCase() === String(projectId).toLowerCase()
    );
  }, [projects, projectId]);

  const projectUsers = useMemo(() => {
    return project ? getProjectTeamUsers(project, state?.users || [], state?.teams || []) : [];
  }, [project, state?.users, state?.teams]);
  
  if (loading || !state) {
    return <div className="p-8 text-center">Loading project...</div>;
  }

  if (!state.currentUser) {
    return <div className="p-8 text-center">Loading user context...</div>;
  }

  const projectTasks = tasks.filter((t) => t.projectId === projectId);

  const filteredTasks = projectTasks.filter((task) => {
    const matchesSearch = task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         task.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterType === 'All' || task.type === filterType;
    return matchesSearch && matchesFilter;
  });

  const orderedFilteredTasks = sortByTaskOrder(filteredTasks);

  // Dynamically group tasks by their actual status
  const tasksByStatus: Record<string, Task[]> = {};
  
  orderedFilteredTasks.forEach((task) => {
    if (!tasksByStatus[task.status]) {
      tasksByStatus[task.status] = [];
    }
    tasksByStatus[task.status].push(task);
  });

  // Define status colors with semantic tokens (org-aware through CSS variables)
  const getStatusColor = (status: string): string => {
    const colorMap: Record<string, string> = {
      'Active': 'border-t-ring',
      'In Progress': 'border-t-ring/80',
      'Completed': 'border-t-border',
      'Deferred': 'border-t-border',
      'Not Required': 'border-t-border',
      'Blocked': 'border-t-ring',
      'Not Started': 'border-t-border',
      'In Review': 'border-t-ring',
      'Done': 'border-t-border',
    };
    return colorMap[status] || 'border-t-border';
  };

  const refreshState = async () => {
    const email = state.currentUser?.email;
    if (email) {
      await reload(email);
    }
  };

  const handleCreateTask = async (task: Task) => {
    if (!canCreateProjectTasks(state.currentUser, project || null)) {
      return;
    }

    console.log("[TaskBoard/handleCreateTask]", task);
    await addTask(task);
    setIsCreateModalOpen(false);
    await refreshState();
  };

  const handleUpdateTask = async (taskId: string, updates: Partial<Task>) => {
    const updatedAt = new Date().toISOString();
    await updateTask(taskId, { ...updates, updatedAt });
    await refreshState();
    if (selectedTask && selectedTask.id === taskId) {
      setSelectedTask({ ...selectedTask, ...updates, updatedAt });
    }
  };

  if (!project) {
    return (
      <div className="mx-auto max-w-7xl p-6 lg:p-8">
        <div className="py-12 text-center">
          <p className="mb-4 text-muted-foreground">Project not found</p>
          <Link to="/app/projects" className="text-primary hover:text-primary/80">
            Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  // Permission checks for task board operations
  const canCreateTasks = canCreateProjectTasks(state.currentUser, project);
  const canEditTasks = canEditTaskParameters(state.currentUser, project);
  const canDeleteTasks = canDeleteProjectTask(state.currentUser, project);
  const isExternalPartner = isExternalPartnerUser(state.currentUser, project);

  return (
    <div className="px-4 sm:px-6 lg:px-8 pt-3 lg:pt-4 pb-6 max-w-7xl mx-auto">
      {/* Permission Warning */}
      {isExternalPartner && (
        <div className="mb-4 flex items-start gap-3 rounded-lg border border-chart-5/40 bg-chart-5/10 p-4">
          <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-chart-5" />
          <div>
            <p className="text-sm font-medium text-foreground">Limited Access</p>
            <p className="text-xs text-muted-foreground">You have restricted access to this project. Some features are unavailable.</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-5">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="mb-1 text-2xl font-semibold text-foreground">Task Board</h1>
            <p className="text-sm text-muted-foreground">{project.name}</p>
          </div>
          {canCreateTasks && (
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90"
            >
              <Plus className="w-5 h-5" />
              New Task
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <SurfaceCard className="mb-6 p-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
          <div className="flex-1 space-y-1.5">
            <span className={APP_FILTER_LABEL_CLASS}>Search</span>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-muted-foreground" />
              <input
                type="text"
                placeholder="Search tasks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full text-sm ${APP_NATIVE_FIELD_CLASS} pl-10 pr-4`}
              />
            </div>
          </div>
          <div className="space-y-1.5 sm:w-52">
            <span className={APP_FILTER_LABEL_CLASS}>Type</span>
            <select
              title="Filter tasks by type"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as TaskType | 'All')}
              className={`w-full text-sm ${APP_NATIVE_FIELD_CLASS}`}
            >
              <option value="All">All Types</option>
              <option value="Feature">Features</option>
              <option value="Bug">Bugs</option>
              <option value="Improvement">Improvements</option>
            </select>
          </div>
        </div>
      </SurfaceCard>

      {/* Kanban Board */}
      <div className="flex gap-4 overflow-x-auto pb-4">
        {Object.entries(tasksByStatus).map(([status, tasks]) => (
          <div key={status} className="flex-shrink-0 w-80">
            <div className={`rounded-lg border border-border border-t-4 bg-card text-card-foreground shadow-sm ${getStatusColor(status)}`}>
              <div className="border-border p-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg text-foreground">{status}</h3>
                  <span className="rounded-full bg-muted px-2 py-1 text-sm text-muted-foreground">
                    {tasks.length}
                  </span>
                </div>
              </div>
              <div className="p-4 space-y-3 min-h-[200px] max-h-[calc(100vh-400px)] overflow-y-auto">
                {tasks.map((task) => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    users={projectUsers}
                    onClick={() => setSelectedTask(task)}
                  />
                ))}
                {tasks.length === 0 && (
                  <p className="py-8 text-center text-sm text-muted-foreground">No tasks</p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {isCreateModalOpen && (
        <CreateTaskModal
          projectId={projectId!}
          onClose={() => setIsCreateModalOpen(false)}
          onCreate={handleCreateTask}
          users={projectUsers}
          teamMemberIds={Array.from(getProjectTeamMemberIds(project, state?.teams || []))}
          existingTasks={projectTasks}
          epics={state.epics.filter(e => e.projectId === projectId)}
          stories={state.stories.filter(s => s.projectId === projectId)}
          project={project}
          orgId={orgId || state.currentOrganization?.id || state.currentUser.organizationId || ''}
        />
      )}

      {selectedTask && (
        <TaskDetailsModal
          task={selectedTask}
          onClose={() => setSelectedTask(null)}
          onUpdate={handleUpdateTask}
          users={state.users}
          currentUser={state.currentUser}
          existingTasks={projectTasks}
          sprints={state.sprints.filter(s => s.projectId === projectId)}
          teams={state.teams || []}
          project={project}
        />
      )}
    </div>
  );
}